import { AsYouType, parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js';

const DEFAULT_COUNTRY: CountryCode = 'US';

/**
 * Formats a phone input as the user types.
 * - If the input starts with "+", the user is providing their own country code.
 * - Otherwise, the default country (US) is assumed and "+1" is prepended.
 * Returns the formatted display string, e.g. "+1 303 555 1234".
 */
export function formatPhoneInput(raw: string, country: CountryCode = DEFAULT_COUNTRY): string {
  const stripped = raw.replace(/[^\d+]/g, '');
  if (!stripped) return '';

  // If user typed "+" they're setting their own country code
  // Otherwise prepend +1 for US default
  const input = stripped.startsWith('+') ? stripped : `+1${stripped}`;

  const formatter = new AsYouType(stripped.startsWith('+') ? undefined : country);
  return formatter.input(input);
}

/**
 * Converts a display-formatted phone string to E.164 format (e.g. "+13035551234").
 * Returns null if the number is not valid.
 */
export function toE164(raw: string, country: CountryCode = DEFAULT_COUNTRY): string | null {
  const digits = raw.replace(/[^\d+]/g, '');
  if (!digits) return null;

  const isInternational = digits.startsWith('+');
  const input = isInternational ? digits : `+1${digits}`;

  const parsed = parsePhoneNumberFromString(input, isInternational ? undefined : country);
  if (!parsed || !parsed.isValid()) return null;

  return parsed.format('E.164');
}

/**
 * Returns true if the raw input represents a valid phone number.
 */
export function isValidPhone(raw: string, country: CountryCode = DEFAULT_COUNTRY): boolean {
  return toE164(raw, country) !== null;
}
